import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = ["EB-1A", "EB-2 NIW", "O-1"];
const empty = { category: "EB-1A", title: "", client_field: "", country_of_origin: "", outcome_year: new Date().getFullYear(), summary: "", highlights: [], is_featured: false };

export default function AdminSuccessStories() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [highlightsText, setHighlightsText] = useState("");

  const load = () => api.get("/success-stories").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      outcome_year: form.outcome_year ? Number(form.outcome_year) : null,
      highlights: highlightsText.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.post("/admin/success-stories", payload);
      toast.success("Success story added");
      setShowForm(false);
      setForm(empty);
      setHighlightsText("");
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this success story?")) return;
    await api.delete(`/admin/success-stories/${id}`);
    toast.success("Deleted");
    load();
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-stories-page">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="eyebrow">Portfolio</div>
          <h1 className="font-display text-4xl mt-3 text-[#0F172A]">Success Stories</h1>
        </div>
        <button data-testid="new-story-btn" onClick={() => setShowForm(!showForm)} className="btn-primary text-sm !py-3 !px-6">
          <Plus size={16} /> {showForm ? "Cancel" : "New story"}
        </button>
      </div>

      {showForm && (
        <div data-testid="new-story-form" className="mt-8 bg-white border border-[#0F172A]/10 p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="eyebrow">Category</label>
            <div className="mt-3">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="story-category" className="editorial-input !h-auto !border-b !border-t-0 !border-l-0 !border-r-0 !rounded-none !bg-transparent focus:!ring-0 !px-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <TextField label="Title" testid="story-title" value={form.title} onChange={(v) => set("title", v)} />
          <TextField label="Client field" testid="story-field" value={form.client_field} onChange={(v) => set("client_field", v)} />
          <TextField label="Country of origin" testid="story-country" value={form.country_of_origin} onChange={(v) => set("country_of_origin", v)} />
          <TextField label="Outcome year" type="number" testid="story-year" value={form.outcome_year} onChange={(v) => set("outcome_year", v)} />
          <TextField label="Highlights (comma-separated)" testid="story-highlights" value={highlightsText} onChange={setHighlightsText} placeholder="12-day approval, 40+ publications" />
          <div className="md:col-span-2">
            <label className="eyebrow">Summary</label>
            <Textarea data-testid="story-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={4} className="editorial-input mt-3 !border-b !border-t-0 !border-l-0 !border-r-0 !rounded-none !bg-transparent focus-visible:!ring-0" />
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <input id="featured" data-testid="story-featured" type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} />
            <label htmlFor="featured" className="text-sm text-[#0F172A]">Feature on homepage</label>
          </div>
          <div className="md:col-span-2">
            <button onClick={save} data-testid="save-story" className="btn-primary">Save story</button>
          </div>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((s) => (
          <div key={s.id} data-testid={`story-${s.id}`} className="card-editorial p-8 flex flex-col">
            <div className="flex items-center justify-between">
              <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#C2A679]">{s.category}</span>
              <button onClick={() => remove(s.id)} data-testid={`delete-${s.id}`} className="text-[#4A4D50] hover:text-[#8B1E1E]">
                <Trash2 size={14} />
              </button>
            </div>
            <h3 className="font-display text-xl mt-6 text-[#0F172A]">{s.title}</h3>
            <div className="mt-2 text-sm text-[#4A4D50]">{s.client_field}{s.country_of_origin ? ` · ${s.country_of_origin}` : ""}</div>
            <p className="mt-4 text-sm text-[#4A4D50] leading-relaxed flex-1">{s.summary}</p>
            <div className="mt-4 text-xs text-[#4A4D50] font-mono-plex">
              {s.outcome_year || "—"} {s.is_featured && "· FEATURED"}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="md:col-span-3 p-10 text-center text-[#4A4D50] bg-white border border-[#0F172A]/10">
            No success stories yet. Add your first case above.
          </div>
        )}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="eyebrow">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="editorial-input mt-3"
      />
    </div>
  );
}
